import { useEffect, useState, useCallback, createRef, useContext } from "react";
import Swal from "sweetalert2";
import { api } from "services/api";
import { useOutsideClick } from "services/outsideClick";
import Pagination from "react-js-pagination";
import UpdateList from "contexts/updateList";
import ReactLoading from "react-loading";
import {
  convertDateToString,
  getValue,
  hasPermission,
} from "utils/globalFunctions";
import { ArrowIcon } from "../ArrowIcon";
import { Button } from "../Button";
import { Filter } from "../Filter";
import { Icons } from "../Icons";
import { Input } from "../Input";
import { ViewLoader } from "../ViewLoader";
import UserProfileContext from "contexts/profile";
import {
  Container,
  Header,
  TableContent,
  CheckboxCell,
  Body,
  Index,
  EditAccess,
  Title,
  Dropdown,
  EmptyTable,
  Status,
  BottomContent,
  TotalItems,
} from "./style";
import isMobile from "is-mobile";

export function Table({
  route,
  match,
  cols,
  history,
  emptyText,
  editHandler,
  editLink,
  noEdit,
  customEditIcon,
  customEditText,
  loadingEdit,
  noLoad,
}) {
  const ref = createRef();


  const profile = useContext(UserProfileContext);
  const { updateList, setUpdateList } = useContext(UpdateList);

  const [loading, setLoading] = useState(true);

  const [selectAll, setSelectAll] = useState(false);

  const [items, setItems] = useState([]);
  const [showDropdown, setShowDropdown] = useState([]);

  const [totalItems, setTotalItems] = useState(0);

  const [runLoadItems, setRunLoadItems] = useState(true);

  const [currentPath, setCurrentPath] = useState("");

  const [max, setMax] = useState(null);

  const [min, setMin] = useState(null);

  useOutsideClick(ref, () => {
    setShowDropdown([]);
  });

  const selectAllHandler = useCallback(
    (checked) => {
      const copyItems = items.map((item) => {
        item.checked = checked;
        return item;
      });
      setItems(copyItems);
      setSelectAll(checked);
    },
    [items]
  );

  const selectItemsHandler = useCallback(
    (checked, index) => {
      const copyItems = [...items];
      copyItems[index].checked = checked;
      setItems(copyItems);
      const hasNotChecked = copyItems.filter((item) => {
        return !item.checked;
      });
      setSelectAll(hasNotChecked.length === 0);
    },
    [items]
  );

  const removeItemsHandler = useCallback(async () => {
    const selectItemsIds = items
      .filter((item) => item.checked)
      .map((item) => item._id);
    if (selectItemsIds[0]) {
      const result = await Swal.fire({
        text: `Deseja arquivar o${selectItemsIds[1] ? "s" : ""} ite ${
          selectItemsIds[1] ? "ns" : "m"
        } selecionados?`,
        title: "Arquivar Itens!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#0451e8",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sim, arquivar itens!",
      });
      if (result.value) {
        Swal.fire(
          `Arquivar Ite${selectItemsIds[1] ? "ns" : "m"}!`,
          `Ite${selectItemsIds[1] ? "ns" : "m"} arquivado${
            selectItemsIds[1] ? "s" : ""
          } com sucesso.`,
          "success"
        );
        setLoading(true);
        const response = await api.delete(route, {
          data: selectItemsIds,
        });
        if (response.data) {
          return setRunLoadItems(true);
        }
        setLoading(false);
      }
    }
  }, [items, route]);

  const dropdownHandler = useCallback((index) => {
    setShowDropdown((prevState) => {
      const newState = [];
      newState[index] = !prevState[index];
      return newState;
    });
  }, []);

  const getTypeItem = useCallback((col, row) => {
    if (col.type === "status") {
      return (
        <Status status={row[col.key]}>
          {row[col.key] ? "Ativo" : "Inativo"}
        </Status>
      );
    } else if (col.type === "custom") {
      return col.response(row);
    } else if (col.type === "date") {
      return convertDateToString(
        row[col.key] ? getValue(row[col.key], col.params) : null
      );
    }
    return (
      <span>{row[col.key] ? getValue(row[col.key], col.params) : null}</span>
    );
  }, []);

  const pageClickHandler = useCallback(
    (pageNumber) => {
      if (+pageNumber !== +match.params.page) {
        history.push(
          match.path
            .replace(":page", pageNumber)
            .replace(":limit", match.params.limit)
            .replace(":sort", match.params.sort)
            .replace(":query", match.params.query)
            .replace(":id", match.params.id)
            .replace(":tab", match.params.tab)
        );
        setRunLoadItems(true);
        setLoading(true);
      }
    },
    [history, match]
  );

  useEffect(() => {
    document.getElementById("root").click();
    const loadItems = async () => {
      setLoading(true);
      const response = await api
        .get(route, {
          params: { ...match.params },
        })
        .catch((err) => err);
      if (!response.data) {
        if (response.message === "Request failed with status code 401") {
          return history.push("/404");
        } else {
          setLoading(false);
          Swal.fire("Erro", response.message, "error");
          return;
        }
      }

      setSelectAll(false);
      setItems(response.data.docs);
      setTotalItems(response.data.total);
      if (response.data.max && response.data.min) {
        setMax(response.data.max);
        setMin(response.data.min);
      }
      setLoading(false);
    };
    if (runLoadItems || match.url !== currentPath || updateList) {
      setUpdateList(false);
      setCurrentPath(match.url);
      setRunLoadItems(false);
      loadItems();
    }
  }, [
    route,
    match,
    currentPath,
    updateList,
    runLoadItems,
    setUpdateList,
    history,
  ]);

  return (
    <>
      {!noLoad && loading ? <ViewLoader isLoading={loading} /> : null}

      {!loading && (
        <Container className="animate__animated animate__fadeInUp">
          <TableContent>
            <Header ref={ref}>
              <tr>
                {hasPermission(profile, route, "edit") && !noEdit ? (
                  <CheckboxCell onClick={() => setShowDropdown([])}>
                    <Input
                      type="checkbox"
                      value={selectAll}
                      onChange={(e) => selectAllHandler(e.target.checked)}
                    />
                  </CheckboxCell>
                ) : null}

                <Index onClick={() => setShowDropdown([])}>
                  <span>Índice</span>
                </Index>
                {cols.map(
                  (
                    {
                      label,
                      filter,
                      key,
                      params,
                      placeholder,
                      mask,
                      populate,
                      options,
                      left,
                    },
                    index
                  ) => (
                    <th key={key}>
                      <Title
                        filter={filter}
                        onClick={filter ? () => dropdownHandler(index) : null}
                      >
                        {label}{" "}
                        {filter && <ArrowIcon show={showDropdown[index]} />}{" "}
                        {match.params.sort ===
                          `${key}${params ? `.${params}` : ""}` && (
                          <Icons type="asc" color="var(--default)" size={15} />
                        )}
                        {match.params.sort ===
                          `-${key}${params ? `.${params}` : ""}` && (
                          <Icons type="desc" color="var(--default)" size={15} />
                        )}
                      </Title>
                      {filter && (
                        <Dropdown
                          filter={filter}
                          show={showDropdown[index]}
                          ref={ref}
                          left={left}
                        >
                          <Filter
                            {...{
                              history,
                              match,
                              colKey: key,
                              params,
                              filter,
                              placeholder,
                              mask,
                              populate,
                              min,
                              max,
                              options,
                            }}
                          />
                        </Dropdown>
                      )}
                    </th>
                  )
                )}
                {!noEdit && (
                  <EditAccess>
                    <span>
                      {customEditText
                        ? customEditText
                        : hasPermission(profile, route, "edit")
                        ? "Editar"
                        : "Acessar"}
                      {hasPermission(profile, route, "edit") ? (
                        <Icons
                          type="trash"
                          size="24"
                          color="#7E8299"
                          onClick={removeItemsHandler}
                        />
                      ) : null}
                    </span>
                  </EditAccess>
                )}
              </tr>
            </Header>
            <Body>
              {items.map((row, index) => (
                <tr key={row._id}>
                  {hasPermission(profile, route, "edit") && !noEdit ? (
                    <CheckboxCell onClick={() => setShowDropdown([])}>
                      <Input
                        type="checkbox"
                        value={items[index].checked}
                        onChange={(e) =>
                          selectItemsHandler(e.target.checked, index)
                        }
                      />
                    </CheckboxCell>
                  ) : null}

                  <td>
                    <span>{index + 1}</span>
                  </td>
                  {cols.map((col) => (
                    <td key={col.key + (col.params || "")}>
                      {getTypeItem(col, row)}
                    </td>
                  ))}
                  {!noEdit && (
                    <td>
                      <Button
                        type="button"
                        bg="default"
                        color="white"
                        border="default"
                        size="sm"
                        disabled={loadingEdit && loadingEdit[index]}
                        to={editLink ? editLink + row._id : null}
                        onClick={
                          editHandler ? () => editHandler(row, index) : null
                        }
                      >
                        {loadingEdit && loadingEdit[index] ? (
                          <ReactLoading
                            type="spin"
                            color="#ffffff"
                            height={14}
                            width={14}
                          />
                        ) : (
                          <Icons
                            type={
                              customEditIcon
                                ? customEditIcon
                                : hasPermission(profile, route, "edit") &&
                                  !noEdit
                                ? "edit"
                                : "etc"
                            }
                            color="white"
                            size={14}
                          />
                        )}
                      </Button>
                    </td>
                  )}
                </tr>
              ))}

              {!items[0] && (
                <tr>
                  <EmptyTable colSpan={cols.length + 3}>
                    <span>{emptyText}</span>
                  </EmptyTable>
                </tr>
              )}
            </Body>
          </TableContent>
          <BottomContent>
            <Pagination
              activePage={+match.params.page}
              itemsCountPerPage={+match.params.limit}
              totalItemsCount={+totalItems}
              pageRangeDisplayed={isMobile() ? 4 : 10}
              onChange={pageClickHandler}
              innerClass="pagination-inner"
              linkClass="pagination-link"
              linkClassFirst="pagination-first"
              linkClassPrev="pagination-prev"
              linkClassNext="pagination-next"
              linkClassLast="pagination-last"
              activeLinkClass="pagination-active"
              disabledClass="pagination-disabled"
              prevPageText={
                <Icons type="angleLeft" color="#009870" size={15} />
              }
              firstPageText={
                <Icons type="angleDoubleLeft" color="#009870" size={15} />
              }
              lastPageText={
                <Icons type="angleDoubleRight" color="#009870" size={15} />
              }
              nextPageText={
                <Icons type="angleRight" color="#009870" size={15} />
              }
            />
            <TotalItems>
              Mostrando{" "}
              {+match.params.page * +match.params.limit -
                +match.params.limit -
                1}{" "}
              a{" "}
              {totalItems > +match.params.page * +match.params.limit
                ? +match.params.page * +match.params.limit
                : totalItems}{" "}
              de {totalItems}
            </TotalItems>
          </BottomContent>
        </Container>
      )}
    </>
  );
}
